import {
  createComponent,
  createEmptyTemplate,
  createRow,
  ensureIndexColumn,
  foldBesideTallMedia,
} from "./catalog";

const STORAGE_PREFIX = "bb_order_print_templates_v1_";

function key(merchantId) {
  return `${STORAGE_PREFIX}${merchantId || "default"}`;
}

function readAll(merchantId) {
  try {
    const raw = localStorage.getItem(key(merchantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeAll(merchantId, list) {
  localStorage.setItem(key(merchantId), JSON.stringify(list));
}

/** Seed a sensible A4 sales-order layout close to current OrderPrintView. */
export function buildDefaultA4Template() {
  const tpl = createEmptyTemplate({
    name: "销售单 A4 基础模板",
    paperSize: "A4",
  });
  tpl.isDesktopDefault = true;
  tpl.isMobileDefault = true;

  const logo = createComponent({
    type: "image",
    field: "logo",
    labelKey: "order.printTemplate.comps.logo",
  });
  logo.span = 3;
  logo.align = "left";

  const merchant = createComponent({
    type: "field",
    field: "merchant_name",
    labelKey: "order.printTemplate.comps.merchantName",
  });
  merchant.showLabel = false;
  merchant.fontSize = 18;
  merchant.bold = true;
  merchant.align = "center";

  const title = createComponent({
    type: "custom-text",
    labelKey: "order.printTemplate.comps.customText",
  });
  title.text = "销售单";
  title.fontSize = 16;
  title.bold = true;
  title.align = "center";

  const barcode = createComponent({
    type: "qrcode",
    field: "order_url",
    labelKey: "order.printTemplate.comps.orderQr",
  });
  barcode.size = 120;
  barcode.besideRows = 3;
  barcode.align = "center";

  const customer = createComponent({
    type: "field",
    field: "receiver",
    labelKey: "order.printTemplate.comps.customerName",
  });
  customer.span = 6;
  customer.labelText = "客户";

  const phone = createComponent({
    type: "field",
    field: "tel",
    labelKey: "order.printTemplate.comps.customerTel",
  });
  phone.span = 6;
  phone.labelText = "电话";

  const orderNo = createComponent({
    type: "field",
    field: "order_no",
    labelKey: "order.printTemplate.comps.orderNo",
  });
  orderNo.span = 6;
  orderNo.labelText = "单号";

  const time = createComponent({
    type: "field",
    field: "created_at",
    labelKey: "order.printTemplate.comps.orderTime",
  });
  time.span = 6;
  time.labelText = "时间";

  const divider1 = createComponent({
    type: "divider",
    labelKey: "order.printTemplate.comps.divider",
  });

  const table = createComponent({
    type: "product-table",
    variant: "line",
    labelKey: "order.printTemplate.comps.productLines",
  });

  const divider2 = createComponent({
    type: "divider",
    labelKey: "order.printTemplate.comps.divider",
  });

  const total = createComponent({
    type: "field",
    field: "total",
    labelKey: "order.printTemplate.comps.totalAmount",
  });
  total.labelText = "合计金额";
  total.bold = true;
  total.fontSize = 14;

  const address = createComponent({
    type: "field",
    field: "address",
    labelKey: "order.printTemplate.comps.address",
  });
  address.labelText = "地址";

  const notes = createComponent({
    type: "field",
    field: "notes",
    labelKey: "order.printTemplate.comps.orderNotes",
  });
  notes.labelText = "备注";

  const printTime = createComponent({
    type: "field",
    field: "print_time",
    labelKey: "order.printTemplate.comps.printTime",
  });
  printTime.labelText = "打印时间";
  printTime.fontSize = 10;

  tpl.layout.rows = [
    createRow([logo]),
    createRow([merchant]),
    createRow([title]),
    createRow([barcode]),
    createRow([customer, phone]),
    createRow([orderNo, time]),
    createRow([divider1]),
    createRow([table]),
    createRow([divider2]),
    createRow([total]),
    createRow([address]),
    createRow([notes]),
    createRow([printTime]),
  ];

  return tpl;
}

export function buildDefault80Template() {
  const tpl = createEmptyTemplate({
    name: "销售单 80mm 基础模板",
    paperSize: "80mm",
  });
  const merchant = createComponent({
    type: "field",
    field: "merchant_name",
    labelKey: "order.printTemplate.comps.merchantName",
  });
  merchant.showLabel = false;
  merchant.bold = true;
  merchant.align = "center";
  merchant.fontSize = 14;

  const table = createComponent({
    type: "product-table",
    variant: "line",
    labelKey: "order.printTemplate.comps.productLines",
  });
  table.columns = ensureIndexColumn([
    { key: "name_sku", label: "商品", width: 36 },
    { key: "barcode", label: "条码", width: 22 },
    { key: "amount", label: "数量", width: 14 },
    { key: "line_total", label: "金额", width: 20 },
  ]);
  table.headerFontSize = 8;
  table.bodyFontSize = 8;

  const total = createComponent({
    type: "field",
    field: "total",
    labelKey: "order.printTemplate.comps.totalAmount",
  });
  total.labelText = "合计";
  total.bold = true;

  tpl.layout.rows = [
    createRow([merchant]),
    createRow([
      createComponent({
        type: "field",
        field: "order_no",
        labelKey: "order.printTemplate.comps.orderNo",
      }),
    ]),
    createRow([
      createComponent({
        type: "divider",
        labelKey: "order.printTemplate.comps.divider",
      }),
    ]),
    createRow([table]),
    createRow([
      createComponent({
        type: "divider",
        labelKey: "order.printTemplate.comps.divider",
      }),
    ]),
    createRow([total]),
    createRow([
      createComponent({
        type: "field",
        field: "receiver",
        labelKey: "order.printTemplate.comps.customerName",
      }),
    ]),
    createRow([
      createComponent({
        type: "field",
        field: "address",
        labelKey: "order.printTemplate.comps.address",
      }),
    ]),
  ];
  return tpl;
}

function ensureBarcodeColumn(columns = []) {
  const cols = ensureIndexColumn(columns);
  if (cols.some((c) => c.key === "barcode")) return cols;
  const after = cols.findIndex((c) =>
    ["name_sku", "name", "sku"].includes(c.key)
  );
  const insertAt = after >= 0 ? after + 1 : 1;
  const next = [...cols];
  next.splice(insertAt, 0, { key: "barcode", label: "条码", width: 16 });
  return next;
}

function migrateTemplate(tpl) {
  if (!tpl?.layout?.rows) return tpl;
  const rows = tpl.layout.rows.map((row) => ({
    ...row,
    children: (row.children || []).map((comp) => {
      if (comp.type === "product-table") {
        return { ...comp, columns: ensureBarcodeColumn(comp.columns) };
      }
      if (comp.type === "barcode") {
        const barcodeFormat =
          comp.barcodeFormat ||
          (comp.field === "order_no" ? "qr" : "code128");
        // Legacy: order-no QR → miniapp public order detail code
        if (comp.field === "order_no" && barcodeFormat === "qr") {
          return {
            ...comp,
            type: "qrcode",
            field: "order_url",
            labelKey: "order.printTemplate.comps.orderQr",
            size: comp.size || 120,
            besideRows: comp.besideRows == null ? 3 : comp.besideRows,
          };
        }
        return {
          ...comp,
          barcodeFormat,
          ...(barcodeFormat === "qr" && !comp.size
            ? { size: 120, height: Math.max(comp.height || 0, 120) }
            : {}),
          ...(barcodeFormat === "qr" && comp.besideRows == null
            ? { besideRows: 3 }
            : {}),
        };
      }
      if (
        (comp.type === "qrcode" || comp.type === "image") &&
        comp.besideRows == null
      ) {
        return { ...comp, besideRows: comp.type === "image" ? 2 : 3 };
      }
      return comp;
    }),
  }));
  return {
    ...tpl,
    lang: tpl.lang === "ug" ? "ug" : "zh",
    layout: { ...tpl.layout, rows: foldBesideTallMedia(rows) },
  };
}

export function listTemplates(merchantId) {
  let list = readAll(merchantId);
  if (!list) {
    list = [buildDefaultA4Template(), buildDefault80Template()];
    writeAll(merchantId, list);
  }
  return list.map(migrateTemplate);
}

export function getTemplate(merchantId, id) {
  return listTemplates(merchantId).find((t) => String(t.id) === String(id));
}

export function saveTemplate(merchantId, template) {
  const list = listTemplates(merchantId);
  const next = {
    ...template,
    updatedAt: new Date().toISOString(),
  };
  const idx = list.findIndex((t) => String(t.id) === String(next.id));
  if (idx >= 0) {
    list[idx] = next;
  } else {
    list.unshift(next);
  }

  if (next.isDesktopDefault) {
    list.forEach((t) => {
      if (String(t.id) !== String(next.id)) t.isDesktopDefault = false;
    });
  }
  if (next.isMobileDefault) {
    list.forEach((t) => {
      if (String(t.id) !== String(next.id)) t.isMobileDefault = false;
    });
  }

  writeAll(merchantId, list);
  return next;
}

export function deleteTemplate(merchantId, id) {
  const list = listTemplates(merchantId).filter(
    (t) => String(t.id) !== String(id)
  );
  writeAll(merchantId, list);
  return list;
}

export function duplicateTemplate(merchantId, id) {
  const src = getTemplate(merchantId, id);
  if (!src) return null;
  const copy = JSON.parse(JSON.stringify(src));
  copy.id = `tpl-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 7)}`;
  copy.name = `${src.name} 副本`;
  copy.isDesktopDefault = false;
  copy.isMobileDefault = false;
  copy.updatedAt = new Date().toISOString();
  return saveTemplate(merchantId, copy);
}

export function setTemplateFlag(merchantId, id, patch) {
  const tpl = getTemplate(merchantId, id);
  if (!tpl) return null;
  return saveTemplate(merchantId, { ...tpl, ...patch });
}

/** Template used for desktop / local browser print from the order list. */
export function getDesktopPrintTemplate(merchantId) {
  const list = listTemplates(merchantId);
  const enabled = list.filter((t) => t.desktopEnabled !== false);
  const pool = enabled.length ? enabled : list;
  return (
    pool.find((t) => t.isDesktopDefault) ||
    pool[0] ||
    null
  );
}
